import React from "react";
import {
  VIEW_BOX,
  glyphGeometry,
  parseSyllable,
  encodeWord,
  markFlags,
  isValidWord,
} from "../../lib/sitelenSike";

const INK = "var(--ss-ink, #38327d)";
const M1 = "var(--ss-m1, #e8734f)";
const M2 = "var(--ss-m2, #7d5bd6)";

// Natural size caps the glyph; max-width lets it shrink fluidly with its
// container on small screens (height: auto keeps the aspect ratio).
const FLUID = { maxWidth: "100%", height: "auto" };

// One syllable glyph. spec = { cons, vowel, coda, m1, m2 }.
// size is the width in px; height follows the shared 120x155 glyph box.
export function SyllableGlyph({ spec, size = 48, strokeWidth = 9, color = INK }) {
  const g = glyphGeometry(spec);
  return (
    <svg
      viewBox={VIEW_BOX}
      width={size}
      height={Math.round(size * (155 / 120))}
      style={FLUID}
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <g transform={`rotate(${g.rot},50,50)`}>
        {g.shapes.map((p, i) =>
          p.d ? (
            <path key={i} d={p.d} />
          ) : (
            <circle key={i} cx={p.c[0]} cy={p.c[1]} r={p.c[2]} />
          )
        )}
      </g>
      {g.dot && <circle cx="97" cy="50" r="5.5" fill={color} stroke="none" />}
      {g.coda && <path d="M32,113 L68,113" />}
      {g.m1 && <circle cx="50" cy="-12" r="6.5" fill={M1} stroke="none" />}
      {g.m2 && <path d="M38,126 L62,114" stroke={M2} strokeWidth={strokeWidth} />}
    </svg>
  );
}

function SyllableRun({ sylls, size, strokeWidth, color }) {
  return sylls.map((s, i) => (
    <SyllableGlyph
      key={i}
      spec={parseSyllable(s)}
      size={size}
      strokeWidth={strokeWidth}
      color={color}
    />
  ));
}

// A whole word: brief -> one marked glyph · spelled -> glyph row under an
// overline · name -> glyph row in a cartouche ring.
export function WordGlyph({ word, size = 48, strokeWidth = 9, color = INK, name = false }) {
  if (!isValidWord(word)) return null;
  const enc = encodeWord(word, { name });
  if (enc.type === "brief") {
    const spec = { ...parseSyllable(enc.key), ...markFlags(enc.mark) };
    return <SyllableGlyph spec={spec} size={size} strokeWidth={strokeWidth} color={color} />;
  }
  const glyphs = (
    <SyllableRun sylls={enc.sylls} size={size} strokeWidth={strokeWidth} color={color} />
  );
  if (enc.type === "name") {
    return (
      <span className="ss-cartouche" style={{ borderRadius: size * 0.5 }}>
        {glyphs}
      </span>
    );
  }
  // spelled: the overline sits in the (guaranteed empty) mark headroom
  return (
    <span className="ss-spelled">
      <span className="ss-overline" style={{ height: Math.max(3, size * 0.1) }} />
      {glyphs}
    </span>
  );
}

// A run of toki pona text: valid words drawn as glyphs, invalid words shown
// in red latin, punctuation passed through in gold.
export function ScriptText({ text, size = 40, strokeWidth = 9, labels = true }) {
  const tokens = text.trim() ? text.trim().split(/\s+/) : [];
  if (!tokens.length) return null;
  return (
    <div className="ss-script-row">
      {tokens.map((tok, i) => {
        const m = tok.match(/^([A-Za-z]*)([^A-Za-z]*)$/);
        const core = m ? m[1] : "";
        const tail = m ? m[2] : "";
        if (!core) {
          return (
            <span key={i} className="ss-punct">
              {tok}
            </span>
          );
        }
        if (!isValidWord(core)) {
          return (
            <span key={i} className="ss-bad" title="not valid toki pona phonology">
              {tok}
            </span>
          );
        }
        return (
          <div key={i} className="ss-script-word">
            <div className="ss-script-word-inner">
              <WordGlyph word={core} size={size} strokeWidth={strokeWidth} />
              {tail && <span className="ss-punct ss-punct-tail">{tail}</span>}
            </div>
            {labels && <div className="ss-latin">{core}</div>}
          </div>
        );
      })}
    </div>
  );
}

// Fixed-height practice card wrapper so quiz glyphs of different words
// don't jump the layout around.
export function GlyphCard({ children, minHeight = 150 }) {
  return (
    <div className="ss-glyph-card" style={{ minHeight }}>
      {children}
    </div>
  );
}
